import { createRouter, createWebHistory, createWebHashHistory, RouteRecordRaw } from 'vue-router'
import NProgress from 'nprogress'
import 'nprogress/nprogress.css'
import store from '@/store'
import { i18n } from '@/i18n'
import { pathToCamel } from '@/utils/tool'
import { userRouterAccounts } from '@/config/menu'

NProgress.configure({ showSpinner: false })

const constantRoutes: RouteRecordRaw[] = [
	{
		path: '/redirect',
		component: () => import('../layout/index.vue'),
		children: [
			{
				path: '/redirect/:path(.*)',
				component: () => import('../layout/components/Router/Redirect.vue')
			}
		]
	},
	{
		path: '/login',
		component: () => import('../views/login/login.vue')
	},
	{
		path: '/loginClient',
		component: () => import('../views/login/loginClient.vue')
	},
	{
		path: '/changePassword',
		component: () => import('../views/login/changePassword.vue')
	},
	{
		path: '/verifyEmail',
		component: () => import('../views/login/verifyEmail.vue')
	},
	{
		path: '/invite',
		component: () => import('../views/web/invite.vue')
	},
	{
		path: '/404',
		component: () => import('../views/404.vue')
	},
	{
		path: '/nopage',
		component: () => import('../views/nopage.vue'),
	}
]

const asyncRoutes: RouteRecordRaw = {
	path: '/',
	component: () => import('../layout/index.vue'),
	redirect: '/home',
	children: [
		{
			path: '/profile/index',
			name: 'ProfilePassword',
			component: () => import('../views/profile/index.vue'),
			meta: {
				title: i18n.global.t('menu.Me'),
				cache: true
			}
		},
		{
			path: '/PageProfile/Index',
			name: 'PageProfile',
			component: () => import('../views/PageProfile/Index.vue'),
			meta: {
				title: i18n.global.t('menu.Me'),
				cache: true
			}
		},
		{
			path: '/kavkaz/receipt/newReceipt',
			name: 'KavkazReceiptNewReceipt',
			component: () => import('../views/kavkaz/receipt/newReceipt.vue'),
			meta: {
				title: '',
				langcode: 'newReceipt',
				cache: true
			}
		},
		{
			path: '/kavkaz/receipt/receiptDetails',
			name: 'receiptDetails',
			component: () => import('../views/kavkaz/receipt/receiptDetails.vue'),
			meta: {
				title: '',
				langcode: 'receiptDetails',
				cache: true
			}
		},
	]
}

export const errorRoute: RouteRecordRaw = {
	path: '/:pathMatch(.*)',
	redirect: '/404'
}

export const router = createRouter({
	history: createWebHistory(),
	routes: constantRoutes
})

// 白名单列表
const whiteList = ['/login', '/loginClient', '/changePassword', '/verifyEmail', '/invite']
// 默认路由
let defaultRoute = '/home'

// 路由加载前
router.beforeEach(async (to, from, next) => {
	NProgress.start()

	// token存在的情况且不是移动端页面
	if (store.userStore?.token) {
		if (to.path === '/login' || to.path === '/loginClient' ) {
			next(defaultRoute)
		} else {
			// 用户信息不存在，则重新拉取用户等信息
			if (!store.userStore.user.id) {
				// // .console.log('正在生成路由');
				await store.userStore.getUserInfoAction()
				const menuRoutes = await store.routerStore.getMenuRoutes()
				// // .console.log(menuRoutes);

				// 判断默认路由
				if(!menuRoutes.some((item: any) => item.path == "/home")){
					if(menuRoutes.length == 0){
						asyncRoutes.redirect = '/nopage'
						defaultRoute = '/nopage'
					}else if(menuRoutes[0]?.children?.length > 0){
						asyncRoutes.redirect = menuRoutes[0]?.children[0].path
						defaultRoute = menuRoutes[0]?.children[0].path
					}else{
						asyncRoutes.redirect = menuRoutes[0].path
						defaultRoute = menuRoutes[0].path
					}
				}
				
				// 根据后端菜单路由，生成KeepAlive路由
				const keepAliveRoutes = getKeepAliveRoutes(menuRoutes, [])
                // console.log('456465456', keepAliveRoutes);
				// 添加菜单路由
				asyncRoutes.children?.push(...keepAliveRoutes)
				router.addRoute(asyncRoutes)

				// 错误路由
				router.addRoute(errorRoute)

				// 保存路由数据
				store.routerStore.setRoutes(constantRoutes.concat(asyncRoutes))

				// 搜索菜单需要使用
				store.routerStore.setSearchMenu(keepAliveRoutes)

				next({ ...to, replace: true })
			} else {
				next()
			}
		}
	} else {
		// 没有token的情况下，可以进入白名单
		if (whiteList.indexOf(to.path) > -1) {
			next()
		} else {
			next({ path: '/login', query: { redirect: to.fullPath } })
		}
	}
})

// 路由加载后
router.afterEach(() => {
	NProgress.done()
})

// 获取扁平化路由，将多级路由转换成一级路由
export const getKeepAliveRoutes = (rs: RouteRecordRaw[], breadcrumb: string[]): RouteRecordRaw[] => {
	const routerList: RouteRecordRaw[] = []

	rs.forEach((item: any) => {
        // item.meta.cache = true;
		if (item.meta.title) {
			breadcrumb.push(item.meta.title)
		}

		if (item.children && item.children.length > 0) {
			routerList.push(...getKeepAliveRoutes(item.children, breadcrumb))
		} else {
			item.meta.breadcrumb.push(...breadcrumb)
			routerList.push(item)
		}

		breadcrumb.pop()
	})
	return routerList
}

// 加载vue组件
const layoutModules = import.meta.glob('/src/views/**/*.vue')

// 根据路径，动态获取vue组件
const getDynamicComponent = (path: string): any => {
	const component = layoutModules[`/src/views/${path}.vue`]
	if (!component) {
		defaultRoute = '/nopage'
		console.error('component error', path)
	}
	return component
}

// 根据菜单列表，生成路由数据
export const generateRoutes = (menuList: any): RouteRecordRaw[] => {
	const routerList: RouteRecordRaw[] = []

	menuList.forEach((menu: any) => {
		let component
		let path
		if (menu.children && menu.children.length > 0) {
			component = () => import('@/layout/index.vue')
			path = '/p/' + menu.id
		} else {
			component = getDynamicComponent(menu.url)
			path = '/' + menu.url
		}
		const route: RouteRecordRaw = {
			path: path,
			name: pathToCamel(path),
			component: component,
			children: [],
			meta: {
				title: i18n.global.t('menu.' + menu.langcode),
				langcode: menu.langcode,
				icon: menu.icon,
				id: '' + menu.id,
				cache: true,
				_blank: menu.openStyle === 1,
				breadcrumb: []
			}
		}

		// 有子菜单的情况
		if (menu.children && menu.children.length > 0) {
			route.children?.push(...generateRoutes(menu.children))
		}

		routerList.push(route)
	})

	return routerList
}

// 添加员工账号路由
export const addAccountsWithRouter = () => {
	// 根据后端菜单路由，生成KeepAlive路由
	const keepAliveRoutes = generateRoutes(userRouterAccounts)
	// 添加菜单路由
	asyncRoutes.children?.push(...keepAliveRoutes)
	router.addRoute(asyncRoutes)
	// 保存路由数据
	store.routerStore.setRoutes(constantRoutes.concat(asyncRoutes))
	// 搜索菜单需要使用
	store.routerStore.setSearchMenu(keepAliveRoutes)
}